/**
 * AnimatedStat — count-up number that fires when scrolled into view, preserving
 * any currency prefix, thousands separators, decimals and unit suffix from a
 * formatted value string ("$2.84M", "27.9%", "186", "4.8/5", "8 min", "2.3x").
 *
 * Gives static metrics a "live" feel without changing the copy. Pair with the
 * LiveStatCard wrapper for the Apple-style liquid-glass treatment.
 */
import React, { useEffect, useRef, useState } from 'react';

function parseValue(raw) {
  const str = String(raw ?? '').trim();
  // Split into leading prefix (non-numeric), the numeric core, and trailing suffix.
  const m = str.match(/^([^0-9-]*)(-?[0-9][0-9,]*\.?[0-9]*)(.*)$/);
  if (!m) return { prefix: '', target: null, suffix: str, decimals: 0, grouped: false };
  const prefix = m[1] || '';
  const numStr = m[2] || '';
  const suffix = m[3] || '';
  const grouped = numStr.includes(',');
  const clean = numStr.replace(/,/g, '');
  const dot = clean.indexOf('.');
  const decimals = dot === -1 ? 0 : clean.length - dot - 1;
  const target = parseFloat(clean);
  return { prefix, target: Number.isFinite(target) ? target : null, suffix, decimals, grouped };
}

function format(value, decimals, grouped) {
  const fixed = value.toFixed(decimals);
  if (!grouped) return fixed;
  const [int, dec] = fixed.split('.');
  const withCommas = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return dec ? `${withCommas}.${dec}` : withCommas;
}

export function AnimatedStat({ value, className = '', style, duration = 1400, delay = 0 }) {
  const { prefix, target, suffix, decimals, grouped } = parseValue(value);
  const [display, setDisplay] = useState(target === null ? value : `${prefix}${format(0, decimals, grouped)}${suffix}`);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    if (target === null) { setDisplay(value); return; }
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !started.current) {
        started.current = true;
        const begin = performance.now() + delay;
        const tick = (now) => {
          const p = Math.min(Math.max((now - begin) / duration, 0), 1);
          const eased = 1 - Math.pow(1 - p, 4);
          setDisplay(`${prefix}${format(target * eased, decimals, grouped)}${suffix}`);
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }
    }, { threshold: 0.3 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [value, target, prefix, suffix, decimals, grouped, duration, delay]);

  return <span ref={ref} className={className} style={style}>{display}</span>;
}

export default AnimatedStat;
